import { useState, type ReactNode } from 'react'
import { useIsNarrow } from '../hooks/useIsNarrow'
import {
  formatDate,
  type Plan,
  type PlanChecklistItem,
  type PlanMilestone,
} from '../features/plans/api'
import { PlanItemPanel, type PanelItem } from './PlanItemPanel'
import { ProgressBar } from './ProgressBar'

function UrgencyTag({ urgency }: { urgency: string }) {
  // Text, not colour alone: the badge has to mean the same thing to a screen
  // reader and in bright sunlight on a show floor.
  if (urgency === 'Overdue') return <span className="badge overdue">Overdue</span>
  if (urgency === 'AtRisk') return <span className="badge at-risk-text">Due soon</span>
  return null
}

function StatusTag({ status }: { status: string }) {
  if (status === 'Complete') return <span className="badge complete">Complete</span>
  if (status === 'Skipped') return <span className="badge">Skipped</span>
  return null
}

/**
 * Who resolved an item, shown on the item itself.
 *
 * The application has always recorded this; showing it here is what makes
 * "who packed this crate, and who checked it back in?" answerable at a glance
 * rather than by opening each item's history.
 */
function ResolvedBy({
  status,
  name,
  at,
}: {
  status: string
  name: string | null
  at: string | null
}) {
  if (!name || status === 'Open') return null

  const verb = status === 'Skipped' ? 'Skipped' : 'Completed'
  const when = at ? ` on ${new Date(at).toLocaleDateString()}` : ''

  return (
    <span>
      {verb} by {name}
      {when}
    </span>
  )
}

/**
 * The plan grouped by lifecycle phase.
 *
 * A phase filter is offered because onsite the useful question is never "show
 * me everything" but "what is left in Badge Printing".
 */
/** A resolved item is dimmed, so what is left stands out at a glance. */
function statusClass(base: string, status: string): string {
  return status === 'Open' ? base : `${base} resolved`
}

function MilestoneCard({
  milestone,
  onSelect,
}: {
  milestone: PlanMilestone
  onSelect: (id: string) => void
}) {
  return (
    <li className={statusClass('card', milestone.status)}>
      <button
        type="button"
        className="card-title link-button"
        onClick={() => onSelect(milestone.id)}
      >
        {milestone.title}
      </button>
      <div className="card-meta">
        <span>Due {formatDate(milestone.dueDate)}</span>
        {milestone.isDueDateOverridden ? <span>Date pinned</span> : null}
        {milestone.assignedToDisplayName ? <span>{milestone.assignedToDisplayName}</span> : null}
        <ResolvedBy
          status={milestone.status}
          name={milestone.completedByDisplayName}
          at={milestone.completedAt}
        />
      </div>
      <div className="card-tags">
        <span className="badge">Milestone</span>
        <StatusTag status={milestone.status} />
        <UrgencyTag urgency={milestone.urgency} />
        {milestone.isAdHoc ? <span className="badge">Added to plan</span> : null}
      </div>
    </li>
  )
}

function ChecklistCard({
  item,
  onSelect,
}: {
  item: PlanChecklistItem
  onSelect: (id: string) => void
}) {
  return (
    <li className={statusClass('card', item.status)}>
      <button type="button" className="card-title link-button" onClick={() => onSelect(item.id)}>
        {item.title}
      </button>
      <div className="card-meta">
        <span>{item.sectionTitle}</span>
        {item.dueDate ? <span>Due {formatDate(item.dueDate)}</span> : null}
        {item.assignedToDisplayName ? <span>{item.assignedToDisplayName}</span> : null}
        <ResolvedBy status={item.status} name={item.completedByDisplayName} at={item.completedAt} />
      </div>
      <div className="card-tags">
        <StatusTag status={item.status} />
        <UrgencyTag urgency={item.urgency} />
        {item.requiresNote ? <span className="badge">Note required</span> : null}
        {item.requiresAttachment ? <span className="badge">Attachment required</span> : null}
        {item.isAdHoc ? <span className="badge">Added to plan</span> : null}
      </div>
    </li>
  )
}

/**
 * Resolved work, put away at the top of its phase.
 *
 * Late in an event most items are resolved, and listing them inline buries what
 * is left. Collapsed by default because the default question is "what's left?".
 * Native <details>, left uncontrolled, so an expanded group stays expanded when
 * the plan refetches after an action.
 */
function ResolvedGroup({
  label,
  count,
  children,
}: {
  label: string
  count: number
  children: ReactNode
}) {
  if (count === 0) return null

  return (
    <details className="resolved-group">
      <summary>
        {label} ({count})
      </summary>
      <ul className="card-list">{children}</ul>
    </details>
  )
}

export function PlanView({ plan }: { plan: Plan }) {
  const [activePhase, setActivePhase] = useState<string>('')
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const isNarrow = useIsNarrow()

  // On a phone, showing every phase at once is unusable, so one is always open.
  // Derived rather than stored: the default follows the plan's own state until
  // the user picks a phase themselves.
  const defaultPhase = isNarrow
    ? ((plan.phases.find((phase) => phase.progress.open > 0) ?? plan.phases[0])?.phase ?? '')
    : ''

  const shownPhase = activePhase || defaultPhase
  const phases = shownPhase ? plan.phases.filter((p) => p.phase === shownPhase) : plan.phases

  // Re-read the selected item from the refreshed plan so the panel reflects an
  // action the moment it lands, rather than the state it opened with.
  const allItems: PanelItem[] = plan.phases.flatMap((phase) => [
    ...phase.milestones.map((m): PanelItem => ({
      id: m.id,
      kind: 'Milestone',
      title: m.title,
      description: m.description,
      status: m.status,
      requiresNote: false,
      requiresAttachment: false,
      dueDate: m.dueDate,
      assignedToUserId: m.assignedToUserId,
      completedByDisplayName: m.completedByDisplayName,
      completedAt: m.completedAt,
      skipReason: m.skipReason,
    })),
    ...phase.checklistItems.map((i): PanelItem => ({
      id: i.id,
      kind: 'ChecklistItem',
      title: i.title,
      description: i.description,
      status: i.status,
      requiresNote: i.requiresNote,
      requiresAttachment: i.requiresAttachment,
      dueDate: i.dueDate,
      assignedToUserId: i.assignedToUserId,
      completedByDisplayName: i.completedByDisplayName,
      completedAt: i.completedAt,
      skipReason: i.skipReason,
    })),
  ])

  const current =
    selectedId === null ? null : (allItems.find((entry) => entry.id === selectedId) ?? null)

  return (
    <section className="plan">
      <div className="panel">
        <h2>Progress</h2>
        <ProgressBar progress={plan.progress} label="Plan" />
        <div className="card-meta">
          <span>
            Timeline: {plan.timelineSource.name} v{plan.timelineSource.version}
          </span>
          {plan.checklistSources.map((source) => (
            <span key={source.name}>
              {source.name} v{source.version}
            </span>
          ))}
        </div>
        <p className="hint">
          Overdue is judged against {formatDate(plan.evaluatedOn)} at the venue.
        </p>
      </div>

      <div
        className={isNarrow ? 'phase-filter narrow' : 'phase-filter'}
        role="tablist"
        aria-label="Filter by phase"
      >
        {isNarrow ? null : (
          <button
            type="button"
            role="tab"
            aria-selected={shownPhase === ''}
            className={shownPhase === '' ? 'chip active' : 'chip'}
            onClick={() => setActivePhase('')}
          >
            All phases
          </button>
        )}
        {plan.phases.map((phase) => (
          <button
            key={phase.phase}
            type="button"
            role="tab"
            aria-selected={shownPhase === phase.phase}
            className={shownPhase === phase.phase ? 'chip active' : 'chip'}
            onClick={() => setActivePhase(phase.phase)}
          >
            {phase.phaseDisplayName}
            {phase.progress.overdue > 0 ? <span className="overdue"> ●</span> : null}
          </button>
        ))}
      </div>

      {phases.map((phase) => {
        // Filtered, never re-sorted: the API's order is already timeline order
        // for milestones and authored order for checklist items.
        const resolvedMilestones = phase.milestones.filter((m) => m.status !== 'Open')
        const openMilestones = phase.milestones.filter((m) => m.status === 'Open')
        const resolvedItems = phase.checklistItems.filter((i) => i.status !== 'Open')
        const openItems = phase.checklistItems.filter((i) => i.status === 'Open')

        return (
          <div key={phase.phase} className="panel">
            <div className="page-header">
              <h2>{phase.phaseDisplayName}</h2>
              <ProgressBar progress={phase.progress} label={phase.phaseDisplayName} />
            </div>

            <ResolvedGroup label="Completed" count={resolvedMilestones.length}>
              {resolvedMilestones.map((milestone) => (
                <MilestoneCard key={milestone.id} milestone={milestone} onSelect={setSelectedId} />
              ))}
            </ResolvedGroup>

            <ResolvedGroup label="Completed checklist items" count={resolvedItems.length}>
              {resolvedItems.map((item) => (
                <ChecklistCard key={item.id} item={item} onSelect={setSelectedId} />
              ))}
            </ResolvedGroup>

            {openMilestones.length > 0 ? (
              <ul className="card-list">
                {openMilestones.map((milestone) => (
                  <MilestoneCard
                    key={milestone.id}
                    milestone={milestone}
                    onSelect={setSelectedId}
                  />
                ))}
              </ul>
            ) : null}

            {openItems.length > 0 ? (
              <ul className="card-list">
                {openItems.map((item) => (
                  <ChecklistCard key={item.id} item={item} onSelect={setSelectedId} />
                ))}
              </ul>
            ) : null}
          </div>
        )
      })}

      {current ? (
        <PlanItemPanel eventId={plan.eventId} item={current} onClose={() => setSelectedId(null)} />
      ) : null}
    </section>
  )
}
